/**
 * Provider - Airwallex SDK Context Provider
 * Initializes the Airwallex SDK and provides createElement to child components
 */
import { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from 'react'

import { withRetry } from '../../utils'

import type { ReactNode } from 'react'

/** Airwallex 环境类型 */
export type AirwallexEnv = 'staging' | 'demo' | 'prod'

/** Airwallex Element 类型 */
export interface AirwallexElement {
  mount: (containerId: string) => HTMLElement
  unmount: () => void
  on: (event: string, handler: (data?: unknown) => void) => void
  blur: () => void
  focus: () => void
  clear: () => void
  confirm: (options: ConfirmOptions) => Promise<ConfirmResult>
}

/** 确认支付参数 (根据 @airwallex/components-sdk 官方类型) */
export interface ConfirmOptions {
  /** Payment Intent ID */
  intent_id?: string
  /** Client Secret */
  client_secret: string
  /** Customer ID (用于 recurring 模式) */
  customer_id?: string
  /** Payment Consent 选项 (用于订阅/recurring 支付) */
  payment_consent?: {
    next_triggered_by: 'merchant' | 'customer'
    merchant_trigger_reason?: 'scheduled' | 'unscheduled' | 'installments'
  }
  /** Payment Method 信息 */
  payment_method?: {
    card?: {
      name?: string
    }
    billing?: {
      email?: string
      first_name?: string
      last_name?: string
      address?: {
        country_code?: string
        postcode?: string
        state?: string
      }
    }
  }
}

/** 确认支付结果 */
export interface ConfirmResult {
  status: 'SUCCEEDED' | 'REQUIRES_CAPTURE' | 'REQUIRES_CUSTOMER_ACTION' | 'PENDING' | 'FAILED'
  payment_consent_id?: string
  error?: {
    message: string
    code: string
  }
}

/** createElement 函数类型 */
export type CreateElementFn = (
  type: string,
  options?: Record<string, unknown>
) => Promise<AirwallexElement>

export interface ProviderProps {
  /** Airwallex 环境 */
  env?: AirwallexEnv
  /** 子组件 */
  children: ReactNode
  /** 最大重试次数 */
  maxRetries?: number
  /** 加载超时时间（毫秒） */
  timeout?: number
}

export interface ProviderContextValue {
  /** createElement 函数 */
  createElement: CreateElementFn | null
  /** 是否正在加载 */
  isLoading: boolean
  /** 加载错误 */
  error: string | null
  /** 是否已就绪 */
  isReady: boolean
  /** 重试次数 */
  retryCount: number
  /** 手动重试函数 */
  retry: () => void
}

const ProviderContext = createContext<ProviderContextValue | null>(null)

/** 全局变量：跨模块共享 SDK 初始化状态 */
declare global {
  interface Window {
    __airwallexInitialized?: boolean
    __airwallexInitPromise?: Promise<unknown>
  }
}

/**
 * 判断是否为生产环境
 */
function isProduction(): boolean {
  if (typeof window === 'undefined') return true
  const hostname = window.location.hostname
  return (
    !hostname.includes('localhost') && !hostname.includes('127.0.0.1') && !hostname.includes('test')
  )
}

export function Provider({ env, children, maxRetries = 3, timeout = 15000 }: ProviderProps) {
  const [createElementFn, setCreateElementFn] = useState<CreateElementFn | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [retryCount, setRetryCount] = useState(0)
  const initRef = useRef(false)

  const initializeSDK = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    setRetryCount(0)

    try {
      const result = await withRetry(
        async () => {
          const sdk = await import('@airwallex/components-sdk')
          const { init, createElement } = sdk as typeof import('@airwallex/components-sdk')

          const finalEnv = env ?? (isProduction() ? 'prod' : 'demo')

          // 使用全局变量确保 init() 只调用一次（跨模块共享）
          if (!window.__airwallexInitialized) {
            if (!window.__airwallexInitPromise) {
              window.__airwallexInitPromise = init({
                env: finalEnv,
                enabledElements: ['payments'],
              })
            }
            await window.__airwallexInitPromise
            window.__airwallexInitialized = true
          }

          return createElement as unknown as CreateElementFn
        },
        {
          maxRetries,
          timeout,
          baseDelay: 1000,
          maxDelay: 5000,
          onRetry: (attempt) => {
            setRetryCount(attempt)
          },
        }
      )

      setCreateElementFn(() => result)
      // SDK 初始化成功，不触发 card_form_ready，由具体表单组件触发
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to initialize Airwallex SDK'
      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }, [env, maxRetries, timeout])

  // 手动重试函数
  const retry = useCallback(() => {
    setCreateElementFn(null)
    void initializeSDK()
  }, [initializeSDK])

  useEffect(() => {
    if (initRef.current) return
    initRef.current = true
    void initializeSDK()
  }, [initializeSDK])

  const contextValue = useMemo<ProviderContextValue>(
    () => ({
      createElement: createElementFn,
      isLoading,
      error,
      isReady: !isLoading && !error && createElementFn !== null,
      retryCount,
      retry,
    }),
    [createElementFn, isLoading, error, retryCount, retry]
  )

  return <ProviderContext.Provider value={contextValue}>{children}</ProviderContext.Provider>
}

/**
 * Hook to access Airwallex context
 */
export function useProvider(): ProviderContextValue {
  const context = useContext(ProviderContext)
  if (!context) {
    throw new Error('useProvider must be used within a Provider')
  }
  return context
}

/** Alias for useProvider - used internally by Airwallex components */
export const useAirwallex = useProvider

Provider.displayName = 'Provider'
